import type {
  GpBulkTip,
  GpCategory,
  GpKpi,
  GpProcessingStep,
  GpPrompt,
  GpReviewPhoto,
  GpSyncedCategory,
} from './types';

export const GP_CATEGORIES: GpCategory[] = [
  { id: 'essential', label: 'Essential', count: 8, uploaded: 3, additional: 0, gap: 'critical' },
  { id: 'interior', label: 'Interior', count: 6, uploaded: 2, additional: 2, gap: 'medium' },
  { id: 'team', label: 'Team', count: 5, uploaded: 4, additional: 1, gap: 'low' },
  { id: 'equipment', label: 'Equipment', count: 4, uploaded: 1, additional: 0, gap: 'medium' },
  { id: 'procedures', label: 'Procedures', count: 5, uploaded: 2, additional: 3, gap: 'medium' },
  { id: 'beforeafter', label: 'Before/After', count: 4, uploaded: 0, additional: 0, gap: 'high' },
];

export const GP_PROMPTS: GpPrompt[] = [
  {
    id: 'ext-sign',
    category: 'essential',
    title: 'Front Signage (exterior)',
    priority: 'essential',
    description:
      "Clear shot of your practice signage from street view. Helps patients find you and improves 'near me' search ranking.",
    tips: [
      'Shoot during daylight, from across the street',
      'Include enough context to show the building',
      'Avoid shots where signage is cropped or angled',
    ],
    example: 'signage',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape · 1920×1080 recommended',
  },
  {
    id: 'ext-building',
    category: 'essential',
    title: 'Building Exterior (full view)',
    priority: 'essential',
    description:
      'Wide shot of your building from the parking lot or sidewalk. Helps patients recognize the location.',
    tips: [
      'Morning light is best for warm tones',
      'Include parking area if visible',
      'Avoid cars blocking the view',
    ],
    example: 'building',
    uploaded: true,
    uploadedDays: 125,
    dimensions: 'Landscape · 1920×1080 recommended',
  },
  {
    id: 'ext-entrance',
    category: 'essential',
    title: 'Entrance Close-up',
    priority: 'essential',
    description:
      "Eye-level shot of the front door with signage. Builds confidence — patients know they're in the right place.",
    tips: [
      'Include accessibility features if present (ramps, auto-doors)',
      'Shoot at chest height',
      'Clean glass, no clutter',
    ],
    example: 'entrance',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape preferred',
  },
  {
    id: 'int-reception',
    category: 'essential',
    title: 'Reception Desk (from entrance)',
    priority: 'essential',
    description:
      'View of reception desk as patients see it when they walk in. Sets tone for the visit.',
    tips: [
      'Staff at desk is a plus (with consent)',
      'Clear desk, no stacks of paper',
      'Shoot from entrance angle',
    ],
    example: 'reception',
    uploaded: true,
    uploadedDays: 45,
    dimensions: 'Landscape',
  },
  {
    id: 'int-waiting',
    category: 'essential',
    title: 'Waiting Area (wide angle)',
    priority: 'essential',
    description:
      "Comfortable, inviting view of your waiting area. One of Google's most-ranked photo types.",
    tips: ['Show seating, magazines/TV, natural light', 'No patients visible', 'Clean, uncluttered'],
    example: 'waiting',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape · wide angle best',
  },
  {
    id: 'int-operatory',
    category: 'essential',
    title: 'Main Operatory (treatment room)',
    priority: 'essential',
    description:
      'Your primary treatment room where most procedures happen. Shows equipment and cleanliness.',
    tips: ['Chair up, lights on, room fully visible', 'Show modern equipment', 'No patients, no staff'],
    example: 'operatory',
    uploaded: true,
    uploadedDays: 18,
    dimensions: 'Landscape · wide angle',
  },
  {
    id: 'team-group',
    category: 'essential',
    title: 'Team Photo (group shot)',
    priority: 'essential',
    description:
      "Group photo of your entire team. Humanizes your practice — patients love knowing who they'll meet.",
    tips: [
      'Everyone in scrubs/lab coats (matching if possible)',
      'Natural smiles, not stiff',
      'Shoot in the practice, not a studio',
    ],
    example: 'teamGroup',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape or portrait',
  },
  {
    id: 'team-doctor',
    category: 'essential',
    title: 'Dentist/Owner Headshot',
    priority: 'essential',
    description:
      'Professional headshot of the main dentist. Used in responses, Q&A, and profile verification.',
    tips: [
      'Smile, eye contact with camera',
      'White coat or scrubs',
      'Plain or practice-themed background',
    ],
    example: 'drHeadshot',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Square or portrait',
  },
  {
    id: 'ba-veneers',
    category: 'beforeafter',
    title: 'Veneers — Before & After',
    priority: 'recommended',
    description:
      'Side-by-side shot showing veneer transformation. Highest-converting photo type for cosmetic inquiries.',
    tips: [
      'Same lighting, angle, and distance for both shots',
      "Close-up of teeth only — not patient's face",
      'Patient consent MUST be obtained',
    ],
    example: 'beforeAfter',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape · side-by-side',
    requiresConsent: true,
  },
  {
    id: 'ba-implant',
    category: 'beforeafter',
    title: 'Dental Implant — Before & After',
    priority: 'recommended',
    description: 'Missing tooth → implant placed. Strong visual proof of your implant work.',
    tips: ['Same angle for both photos', 'Crop tightly to mouth only', '3-6 months between shots ideal'],
    example: 'beforeAfter',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape · side-by-side',
    requiresConsent: true,
  },
  {
    id: 'ba-whitening',
    category: 'beforeafter',
    title: 'Whitening — Before & After',
    priority: 'nice',
    description: 'Shade change comparison. Quick, strong visual for cosmetic searches.',
    tips: [
      'Same time of day, same light source',
      'Lip retractor for clean comparison',
      'Include shade guide if possible',
    ],
    example: 'beforeAfter',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape · side-by-side',
    requiresConsent: true,
  },
  {
    id: 'ba-ortho',
    category: 'beforeafter',
    title: 'Invisalign / Braces — Before & After',
    priority: 'nice',
    description: 'Alignment transformation. Ortho cases have strong emotional impact.',
    tips: [
      'Same smile angle for both',
      'Capture the full smile',
      'Include treatment duration in alt text',
    ],
    example: 'beforeAfter',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape · side-by-side',
    requiresConsent: true,
  },
  {
    id: 'team-group2',
    category: 'team',
    title: 'Team Group Shot (casual)',
    priority: 'recommended',
    description: 'Second team photo — casual, relaxed, showing personalities.',
    tips: ['Group laughing or interacting', 'Avoid stiff posed shots'],
    example: 'teamGroup',
    uploaded: true,
    uploadedDays: 95,
    dimensions: 'Landscape',
  },
  {
    id: 'team-hygienist',
    category: 'team',
    title: 'Hygienist at Work',
    priority: 'recommended',
    description: 'Hygienist cleaning instruments or preparing the room — NOT working on a patient.',
    tips: ['Hands and equipment only', 'No patient visible'],
    example: 'hygienist',
    uploaded: true,
    uploadedDays: 22,
    dimensions: 'Landscape',
  },
  {
    id: 'team-desk',
    category: 'team',
    title: 'Front Desk Staff',
    priority: 'recommended',
    description: 'Reception staff at the desk, smiling at camera.',
    tips: ['2-3 people max', 'Natural smiles'],
    example: 'reception',
    uploaded: true,
    uploadedDays: 62,
    dimensions: 'Landscape',
  },
  {
    id: 'team-doc2',
    category: 'team',
    title: 'Second Dentist Headshot',
    priority: 'nice',
    description: 'If you have additional dentists, each gets a professional headshot.',
    tips: ["Match the primary dentist's style", 'Consistent lighting'],
    example: 'drHeadshot',
    uploaded: true,
    uploadedDays: 14,
    dimensions: 'Square or portrait',
  },
  {
    id: 'team-training',
    category: 'team',
    title: 'Team in Training / Meeting',
    priority: 'nice',
    description:
      'Staff meeting, training session, or continuing education — shows professional development.',
    tips: ['Show screens, notes, engagement', 'Candid, not posed'],
    example: 'teamGroup',
    uploaded: false,
    uploadedDays: null,
    dimensions: 'Landscape',
  },
];

export const GP_KPIS: GpKpi[] = [
  {
    key: 'total',
    label: 'Total Photos',
    value: '18',
    unit: 'on GBP',
    sub: '8 match our prompts + 5 additional',
    tone: 'neutral',
  },
  {
    key: 'essential-coverage',
    label: 'Essential Coverage',
    value: '38%',
    sub: '3 of 8 essentials uploaded',
    tone: 'terra',
  },
  {
    key: 'stale',
    label: 'Stale Photos',
    value: '3',
    sub: 'Photos older than 90 days · refresh these',
    tone: 'terra',
  },
  {
    key: 'gap',
    label: 'vs. Smile Central (#1 local)',
    value: '−33',
    sub: 'They have 45 photos · You have 18',
    tone: 'terra',
  },
];

// ── Bulk upload ──────────────────────────────────────────────────────────────

const GP_BULK_PHOTOS_BASE: Omit<GpReviewPhoto, 'patchId'>[] = [
  {
    id: 1,
    example: 'reception',
    fileName: 'IMG_0142.JPG',
    size: '3.2 MB',
    category: 'Interior',
    matchedPrompt: 'Reception Desk (from entrance)',
    confidence: 96,
    description: 'Reception desk with warm lighting',
    status: 'ready',
  },
  {
    id: 2,
    example: 'operatory',
    fileName: 'IMG_0143.JPG',
    size: '3.8 MB',
    category: 'Interior',
    matchedPrompt: 'Main Operatory',
    confidence: 94,
    description: 'Main treatment room, chair up, modern equipment',
    status: 'ready',
  },
  {
    id: 3,
    example: 'teamGroup',
    fileName: 'IMG_0144.JPG',
    size: '2.9 MB',
    category: 'Team',
    matchedPrompt: 'Team Photo (group shot)',
    confidence: 98,
    description: 'Full team group photo in scrubs',
    status: 'ready',
  },
  {
    id: 4,
    example: 'signage',
    fileName: 'IMG_0145.JPG',
    size: '4.1 MB',
    category: 'Exterior',
    matchedPrompt: 'Front Signage (exterior)',
    confidence: 91,
    description: 'Front signage, daylight',
    status: 'ready',
  },
  {
    id: 5,
    example: 'hallway',
    fileName: 'IMG_0146.JPG',
    size: '3.5 MB',
    category: 'Interior',
    matchedPrompt: null,
    confidence: 78,
    description: 'Hallway view',
    status: 'ready',
    additional: true,
  },
  {
    id: 6,
    example: 'xray',
    fileName: 'IMG_0147.JPG',
    size: '2.1 MB',
    category: 'Equipment',
    matchedPrompt: 'Digital X-Ray Machine',
    confidence: 89,
    description: 'X-ray machine',
    status: 'warning',
    qualityIssue: 'Slightly dark — consider retaking with better lighting',
  },
  {
    id: 7,
    example: 'sterilization',
    fileName: 'IMG_0148.JPG',
    size: '3.0 MB',
    category: 'Equipment',
    matchedPrompt: 'Sterilization Area',
    confidence: 92,
    description: 'Sterilization equipment',
    status: 'ready',
  },
  {
    id: 8,
    example: 'building',
    fileName: 'IMG_0149.JPG',
    size: '4.4 MB',
    category: 'Exterior',
    matchedPrompt: 'Building Exterior (full view)',
    confidence: 87,
    description: 'Building exterior, morning light',
    status: 'ready',
  },
  {
    id: 9,
    example: 'entrance',
    fileName: 'IMG_0150.JPG',
    size: '2.7 MB',
    category: null,
    matchedPrompt: null,
    confidence: 42,
    description: '',
    status: 'uncategorized',
    qualityIssue: 'Could not confidently categorize this photo. Please assign manually.',
  },
  {
    id: 10,
    example: 'waiting',
    fileName: 'IMG_0151.JPG',
    size: '3.3 MB',
    category: 'Interior',
    matchedPrompt: null,
    confidence: 82,
    description: 'Kids play area',
    status: 'ready',
    additional: true,
  },
  {
    id: 11,
    example: 'beforeAfter',
    fileName: 'IMG_0152.JPG',
    size: '1.8 MB',
    category: 'Before/After',
    matchedPrompt: 'Veneers — Before & After',
    confidence: 95,
    description: 'Veneers transformation',
    status: 'warning',
    qualityIssue:
      'Patient face partially visible — please crop to mouth only before uploading',
    requiresConsent: true,
  },
  {
    id: 12,
    example: 'hallway',
    fileName: 'IMG_0153.JPG',
    size: '0.4 MB',
    category: 'Interior',
    matchedPrompt: null,
    confidence: 65,
    description: 'Small image',
    status: 'rejected',
    qualityIssue: 'Resolution too low (480×320). Google requires minimum 720p.',
  },
];

export const GP_BULK_PHOTOS: GpReviewPhoto[] = GP_BULK_PHOTOS_BASE.map((photo) => ({
  ...photo,
  patchId: photo.id,
}));

export const GP_PROCESSING_STEPS: GpProcessingStep[] = [
  { label: 'Categorizing photos', done: true },
  { label: 'Checking image quality', done: true },
  { label: 'Matching to ELVA prompts', done: false, active: true },
  { label: 'Generating descriptions', done: false },
  { label: 'Duplicate detection', done: false },
];

export const GP_SYNCED_CATEGORIES: GpSyncedCategory[] = [
  { label: 'Interior', count: 3 },
  { label: 'Exterior', count: 2 },
  { label: 'Team', count: 1 },
  { label: 'Equipment', count: 2 },
  { label: 'Additional', count: 1 },
];

export const GP_BULK_TIPS: GpBulkTip[] = [
  {
    icon: 'bulb',
    title: 'No preparation needed',
    desc: 'Upload whatever photos you have — ELVA will sort them into the right categories automatically.',
  },
  {
    icon: 'search',
    title: 'Quality checked for you',
    desc: 'We flag photos that are blurry, too dark, or too low-resolution before they hit Google.',
  },
  {
    icon: 'check-circ',
    title: 'Review before sync',
    desc: 'You approve every photo before it goes live on your Google Business Profile.',
  },
];
